import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, AlertTriangle, MapPin, Clock, Phone, Ruler, ShieldCheck, ArrowRight, Droplets, Sprout, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
export function DeliveryPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const stagger = {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      staggerChildren: 0.1
    }
  };
  const item = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="pb-16 bg-[#E8E4DB] dark:bg-[#222222] min-h-screen" data-id="element-3235" data-mp="2q4zrz7hmwbif">
      {/* Hero Section — gravel photo background with a uniform black
         overlay, same treatment as the rest of the site's photo heroes
         so the white headline stays readable while the material under
         it cues "this page is about delivery prep, on real gravel." */}
      <section className="relative py-20 overflow-hidden border-b border-stone-200 dark:border-white/15" data-id="element-3236" data-mp="cgdj1t9w947k">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/gukm22ExLHvasq3sgFamZQ/PXL_20260530_192218218~2.jpg')"
      }} aria-hidden="true" data-id="element-3237" data-mp="196390l7bwxuh" />
        {/* Black overlay — matches the flat opacity Hero.tsx uses on
         mobile, since this page is always center-aligned and doesn't
         need a directional gradient. */}
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" data-id="element-3238" data-mp="3kwfbnxsixgyr" />
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" data-id="element-3239" data-mp="3fvrppqj5jqbe" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3240" data-mp="3heroigifz8wk">
          
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-3241" data-mp="3mb8c99lqbl94">
            Prepare for Your <span className="text-brand-orange" data-id="element-3242" data-mp="2ykvxbes2go91">Delivery</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-medium" data-id="element-3243" data-mp="33f03hvtics92">
            Follow this simple guide to ensure a smooth, safe, and successful
            material delivery to your home or job site.
          </motion.p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16" data-id="element-3244" data-mp="212gtvk6bn4m4">
        {/* Before Delivery Day */}
        <motion.section {...fadeIn} data-id="element-3245" data-mp="1w3maag0j5c0y">
          <div className="flex items-center gap-4 mb-8" data-id="element-3246" data-mp="1w4nminta84k9">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/30 dark:border-white/15" data-id="element-3247" data-mp="2a2vh4p2uits9">
              <CalendarDays className="w-6 h-6 text-brand-orange" data-id="element-3248" data-mp="2k0060wo2t3cr" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-3249" data-mp="2jxx9ruwbydzl">
              Before Delivery Day
            </h2>
          </div>
          <motion.div variants={stagger} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3250" data-mp="2tpjmxws8an4z">
            {[{
            icon: <Truck className="w-5 h-5" data-id="element-3251" data-mp="32r13thdgmsuq" />,
            text: 'Clear the dump area of vehicles, toys, and obstacles'
          }, {
            icon: <MapPin className="w-5 h-5" data-id="element-3252" data-mp="w2ipag3df1wn" />,
            text: 'Mark the exact spot where you want material dumped (use a cone, tarp, or flag)'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-3253" data-mp="10zr72fklx3xd" />,
            text: 'Ensure gate access if fenced and provide gate codes if applicable'
          }, {
            icon: <Ruler className="w-5 h-5" data-id="element-3254" data-mp="19ufd47y0apn8" />,
            text: 'Measure clearance: Trucks are 10ft wide, 12ft tall, and 30ft long'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-3255" data-mp="d5hhknifmnp0" />,
            text: 'Check for low-hanging overhead wires or tree branches'
          }, {
            icon: <CheckCircle2 className="w-5 h-5" data-id="element-3256" data-mp="m3td93y5obob" />,
            text: 'Confirm someone 18+ will be on-site to receive the delivery'
          }].map((point, i) => <motion.div key={i} variants={item} className="flex items-start gap-3 p-4 bg-[#f2f4f0] dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-3257" data-mp="r4piply5o5vu" data-mpi={i}>
                <div className="text-brand-orange shrink-0 mt-0.5" data-id="element-3258" data-mp="22gzzsv3jedkg">
                  {point.icon}
                </div>
                <p className="text-brand-charcoal dark:text-stone-200 font-medium" data-id="element-3259" data-mp="2com3l0dwsqie">
                  {point.text}
                </p>
              </motion.div>)}
          </motion.div>
        </motion.section>

        {/* Day of Delivery */}
        <motion.section {...fadeIn} data-id="element-3260" data-mp="1yvmrz9h8zgg4">
          <div className="flex items-center gap-4 mb-8" data-id="element-3261" data-mp="1yule6x8ukfup">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20 dark:border-white/15" data-id="element-3262" data-mp="1twjm5mtg1673">
              <Clock className="w-6 h-6 text-brand-orange" data-id="element-3263" data-mp="1oz07ilsu2rem" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-3264" data-mp="1jym1186vrpip">
              Day of Delivery
            </h2>
          </div>
          <div className="bg-[#f2f4f0] dark:bg-[#282828] rounded-2xl border border-stone-100 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-3265" data-mp="3ecdekisbytjf">
            <div className="divide-y divide-stone-100 dark:divide-white/5" data-id="element-3266" data-mp="3aarykoki1z50">
              {[{
              icon: <Phone className="w-6 h-6" data-id="element-3267" data-mp="17ma3xnga3bld" />,
              title: 'Driver will call',
              desc: 'Expect a call 15-30 minutes before the driver arrives at your location.'
            }, {
              icon: <MapPin className="w-6 h-6" data-id="element-3268" data-mp="yorfe93fef4q" />,
              title: 'Point the way',
              desc: 'Direct the driver to your marked dump spot upon arrival.'
            }, {
              icon: <AlertTriangle className="w-6 h-6" data-id="element-3269" data-mp="1qfdx77pl3mfg" />,
              title: 'Stay clear',
              desc: 'Keep children, pets, and yourself at a safe distance during the dumping process.'
            }, {
              icon: <CheckCircle2 className="w-6 h-6" data-id="element-3270" data-mp="1hd4rcv59gh8e" />,
              title: 'Inspect material',
              desc: 'Verify the material is correct before the driver leaves the site.'
            }].map((step, i) => <div key={i} className="p-6 flex items-start gap-4" data-id="element-3271" data-mp="5wxxmd7pxllf" data-mpi={i}>
                  <div className="w-10 h-10 rounded-full bg-brand-orange/20 dark:bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0" data-id="element-3272" data-mp="aydj26dt5u4c">
                    {step.icon}
                  </div>
                  <div data-id="element-3273" data-mp="3ujku3762pq8c">
                    <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-3274" data-mp="3um74rfo9glus">
                      {step.title}
                    </h3>
                    <p className="text-stone-700 dark:text-stone-400 font-medium" data-id="element-3275" data-mp="7gg5j0errcvx">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Access Tips */}
        <motion.section {...fadeIn} data-id="element-3276" data-mp="3pkri5j6fq7aa">
          <div className="flex items-center gap-4 mb-8" data-id="element-3277" data-mp="3plt2yfd96857">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20 dark:border-white/15" data-id="element-3278" data-mp="3kuksrh43miqz">
              <Truck className="w-6 h-6 text-brand-orange" data-id="element-3279" data-mp="2x2vw5ok8v848" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-3280" data-mp="2s4lxh3zb9pdv">
              Access Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3281" data-mp="2bl5hy2i8bbma">
            <div className="p-6 bg-[#f2f4f0] dark:bg-[#262626] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-3282" data-mp="2blnk1frkok24">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-3283" data-mp="2ljbj2hw7odq3">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-3284" data-mp="2plgcpgmntacq" />
                Firm Ground Required
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-3285" data-mp="2uj83267nooet">
                Fully loaded trucks are extremely heavy. They need firm ground
                or pavement to drive on, not soggy lawns or soft dirt, to avoid
                getting stuck or causing damage.
              </p>
            </div>
            <div className="p-6 bg-[#f2f4f0] dark:bg-[#262626] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-3286" data-mp="39b0ew888rkbs">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-3287" data-mp="11nfflnrycskp">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-3288" data-mp="xm2axh0livfd" />
                Steep Driveways
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-3289" data-mp="slxwdwj90ars">
                Very steep driveways may be unsafe for a dump truck to navigate
                or dump on. In these cases, a street-level dump may be required.
              </p>
            </div>
            <div className="p-6 bg-[#f2f4f0] dark:bg-[#262626] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-3290" data-mp="1gbs4y1wjbd68">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-3291" data-mp="1bkafob3vj113">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-3292" data-mp="16lz6m2lekujx" />
                Cul-de-sacs & Tight Turns
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-3293" data-mp="243phxwbq0b8x">
                Large dump trucks need significant turning radius. Ensure there
                is adequate room to maneuver in cul-de-sacs or tight
                neighborhoods.
              </p>
            </div>
            <div className="p-6 bg-brand-orange/10 dark:bg-brand-orange/10 rounded-xl border border-brand-orange/30 dark:border-white/0 shadow-sm flex flex-col justify-center" data-id="element-3294" data-mp="7zxfgcmozzwt">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-3295" data-mp="hufidfkabrfk">
                <Phone className="w-5 h-5 text-brand-orange" data-id="element-3296" data-mp="vtf7spfmgacs" />
                Unsure about access?
              </h3>
              <p className="text-stone-700 dark:text-stone-300 text-sm font-medium mb-3" data-id="element-3297" data-mp="15lbjj3amryzh">
                If you have concerns about your delivery location, call us first
                and we can review a photo of your site.
              </p>
              <a href="tel:206-650-0129" className="inline-flex items-center text-brand-orange font-bold hover:underline" data-id="element-3298" data-mp="1en19e4ov6pv5">
                Call (206) 650-0129 <ArrowRight className="w-4 h-4 ml-1" data-id="element-3299" data-mp="1ail4o6qzlohi" />
              </a>
            </div>
          </div>
        </motion.section>

        {/* After Delivery */}
        <motion.section {...fadeIn} data-id="element-3300" data-mp="1y6i1anc0qwge">
          <div className="flex items-center gap-4 mb-8" data-id="element-3301" data-mp="1y5fb6hmisy1j">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20 dark:border-white/15" data-id="element-3302" data-mp="1k78kblfznybd">
              <Sprout className="w-6 h-6 text-brand-orange" data-id="element-3303" data-mp="1ai76o1wx1t0k" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-3304" data-mp="1ag46ip21uji4">
              After Delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3305" data-mp="11ituh1heqqc5">
            <div className="p-6 bg-[#f2f4f0] dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-3306" data-mp="15iuh39enu7ua">
              <Clock className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-3307" data-mp="rjuwqdy6kf3x" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-3308" data-mp="wipf5es8k1ni">
                Spread Quickly
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-3309" data-mp="2y8wju5c15i3d">
                Spread material within 48 hours to avoid settling and compaction
                in the pile.
              </p>
            </div>
            <div className="p-6 bg-[#f2f4f0] dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-3310" data-mp="2kg6xfzslab5q">
              <Droplets className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-3311" data-mp="3h5fy191oxw6s" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-3312" data-mp="3d4ck4s37ea87">
                Wet Before Compacting
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-3313" data-mp="38480wuawv1ea">
                Lightly wet gravel before using a plate compactor for maximum
                density and stability.
              </p>
            </div>
            <div className="p-6 bg-[#f2f4f0] dark:bg-[#282828] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-3314" data-mp="1rrj5jldc4nj7">
              <Sprout className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-3315" data-mp="1honpaw111fk4" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-3316" data-mp="1mm5zepqs60tp">
                Protect Trees
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-3317" data-mp="1rl0gnn93nhbp">
                When spreading bark or mulch, keep it at least 3 inches away
                from tree trunks to prevent rot.
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="bg-brand-charcoal dark:bg-[#282828] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" style={{
        backgroundColor: "#032D20"
      }} data-id="element-3318" data-mp="25blyd8mthl80">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-3319" data-mp="2fa1rti9bh6ko"></div>
          <div className="relative z-10" data-id="element-3320" data-mp="kx2411us962b">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-3321" data-mp="kxvg8icignru">
              Ready to order?
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-3322" data-mp="26fcpiacqow19">
              Now that you know how to prepare, head over to our Materials Hub
              to select your products and schedule your delivery.
            </p>
            <a href="/#materials" className="inline-flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-[0_0_15px_rgba(255,140,50,0.3)] hover:shadow-[0_0_20px_rgba(255,140,50,0.3)]" data-id="element-3323" data-mp="2fhc6j2nr8047">
              Browse Materials Hub
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-3324" data-mp="21hklihytljgp" />
            </a>
          </div>
        </motion.section>
      </div>
    </div>;
}